import { useState, useCallback } from 'react';
import { Team, RandomizationHistory } from '../types';
import { generateTeams } from '../utils/teamGenerator';
import { useS3State } from './useS3State';

/**
 * Custom hook for managing team randomization with S3 persistence
 * 
 * Features:
 * - Loads team assignments from S3
 * - Loads randomization history from S3
 * - Generates teams with constraints and diversity optimization
 * - Updates pairing history after generation
 * - Persists teams and history to S3
 * 
 * @returns Object with teams, generateTeamsForItem function, isLoading, and error
 */
export function useTeamRandomization() {
  const [teamAssignments, setTeamAssignments, isLoadingTeams, teamsError] = useS3State(
    'teamAssignments',
    {}
  );
  
  const [randomizationHistory, setRandomizationHistory, isLoadingHistory, historyError] = useS3State(
    'randomizationHistory',
    { assignments: [], participantPairings: {} }
  );

  const [isGenerating, setIsGenerating] = useState(false);

  /**
   * Generate teams for a specific agenda item
   * Updates both team assignments and randomization history
   */
  const generateTeamsForItem = useCallback(
    async (agendaItemId: string): Promise<Team[]> => {
      setIsGenerating(true);

      try {
        // Generate teams using current history for optimization
        const teams = generateTeams(randomizationHistory);

        // Update pairing history
        const updatedPairings = { ...randomizationHistory.participantPairings };
        
        // Track all pairings in the new teams
        for (const team of teams) {
          const members = team.members.map(m => m.name);
          
          // Update pairing counts for all pairs in this team
          for (let i = 0; i < members.length; i++) {
            for (let j = i + 1; j < members.length; j++) {
              const pairKey = `${members[i]}-${members[j]}`;
              updatedPairings[pairKey] = (updatedPairings[pairKey] || 0) + 1;
            }
          }
        }

        // Create new assignment record
        const newAssignment = {
          agendaItemId,
          teams,
          timestamp: new Date().toISOString()
        };

        // Update randomization history
        const updatedHistory: RandomizationHistory = {
          assignments: [...randomizationHistory.assignments, newAssignment],
          participantPairings: updatedPairings
        };

        // Update team assignments
        const updatedTeamAssignments = {
          ...teamAssignments,
          [agendaItemId]: teams
        };

        // Persist both to S3
        await Promise.all([
          setTeamAssignments(updatedTeamAssignments),
          setRandomizationHistory(updatedHistory)
        ]);

        setIsGenerating(false);
        return teams;
      } catch (err) {
        console.error('Failed to generate teams:', err);
        setIsGenerating(false);
        throw err;
      }
    },
    [teamAssignments, randomizationHistory, setTeamAssignments, setRandomizationHistory]
  );

  /**
   * Get teams for a specific agenda item
   */
  const getTeamsForItem = useCallback(
    (agendaItemId: string): Team[] | undefined => {
      return teamAssignments[agendaItemId];
    },
    [teamAssignments]
  );

  const isLoading = isLoadingTeams || isLoadingHistory || isGenerating;
  const error = teamsError || historyError;

  return {
    teamAssignments,
    getTeamsForItem,
    generateTeamsForItem,
    isLoading,
    error
  };
}
